import { useState, useMemo, useCallback } from "react";
import { CopyIcon, GreenEllipse, PhysicalCardIcon, RedEllipse, VirtualCardIcon } from "../../../../Icons";
import { useSelector } from "react-redux";
import { FormatBalance, GetCurrenySymbol } from "../../../../../helper";
import ToolTip from "../../../../Header/ToolTip";

export const ActiveCard = ({ hideBalance = false }) => {
  const { activeCard } = useSelector((state) => state.activeCard);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState("Copy Card Number");

  // --- Helpers ---
  const formatCardNumber = useCallback(
    (number) => number?.replace(/(\d{4})(?=\d)/g, "$1 ") || "",
    []
  );

  const formatCardExpiry = useCallback(
    (expiry) => expiry?.replace(/(\d{2})(?=\d)/g, "$1/") || "",
    []
  );

  const cardBgClass = useMemo(() => {
    const { card_provider } = activeCard || {};
    switch (card_provider) {
      case "VM":
        return "bg-visa-card-bg"
      case "MM":
        return "bg-master-card-bg";
      case "EM":
        return "bg-emoney-card-bg";
      default:
        return "bg-card-default-bg";
    }
  }, [activeCard]);

  const showCopyIcon =
    activeCard?.card_provider === "MM" &&
    activeCard?.card_cvv &&
    activeCard.card_cvv !== "***";

  const nameLength = activeCard.name ? activeCard.name.length : 0;
  const nameMarginY = nameLength > 13 ? "my-0 !text-xs" : "my-4";

  // --- Copy handler ---
  const handleCopy = useCallback(() => {
    if (!showCopyIcon || !activeCard?.card_number) return;

    navigator.clipboard.writeText(activeCard.card_number);
    setTooltipText("Copied!");
    setShowTooltip(true);

    setTimeout(() => {
      setShowTooltip(false);
      setTooltipText("Copy Card Number");
    }, 1000);
  }, [showCopyIcon, activeCard?.card_number]);

  const isEmoneyOrMM =
    activeCard?.card_provider === "MM" || activeCard?.card_provider === "EM";

  const cardProviderClass = useMemo(() => {
    if (activeCard?.card_provider === "MM") return "pt-16";
    if (activeCard?.card_provider === "EM") return "pt-10";
    return "mt-14 mb-5";
  }, [activeCard?.card_provider]);

  return (
    <div className="active-card space-y-3">
      {/* Status */}
      <div className="flex w-[314px] max-[360px]:w-[300px] items-center justify-between">
        <div className="flex items-center space-x-2">
          {activeCard.is_physical ? <PhysicalCardIcon /> : <VirtualCardIcon />}
          <p className="text-sm font-medium text-text-primary">
            {activeCard.is_physical ? "Physical Card" : "Virtual Card"}
          </p>
        </div>
        <span className="status-pill">
          {activeCard.status === "Active" ? <GreenEllipse /> : <RedEllipse />}
          <p className="status-text">{activeCard.status}</p>
        </span>
      </div>

      {/* Card */}
      <div
        className={`${cardBgClass} relative rounded-2xl w-[314px] h-[196px] max-[360px]:w-[300px] bg-cover bg-center bg-no-repeat`}
      >
        <div
          className={`w-full h-full rounded-2xl p-2 ${
            activeCard.status !== "Active" ? "inactive-overlay" : "text-white"
          }`}
        >
          {/* Card Number + Copy */}
          <div
            className={`${cardProviderClass}`}
            onMouseEnter={() => showCopyIcon && setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={handleCopy}
          >
            <div className="card-number">
              <p>{formatCardNumber(activeCard.card_number)}</p>
              {showCopyIcon && <CopyIcon className="copy-icon" />}
            </div>
            <ToolTip
              text={tooltipText}
              showTooltip={showTooltip}
              arrowPosition="down"
              className="top-9 min-w-max"
            />            
          </div>

          {/* Expiry & CVC */}
          {isEmoneyOrMM && (
            <div className="flex pb-2">
              <div className="flex items-center">
                <p className="card-meta-label">Expiry:</p>
                <p className="card-meta-value">
                  {activeCard.card_expiry &&
                    formatCardExpiry(activeCard.card_expiry)}
                </p>
              </div>
              <div className="flex items-center ml-2">
                <p className="card-meta-label">CVC:</p>
                <p className="card-meta-value">{activeCard.card_cvv}</p>
              </div>
            </div>
          )}

          {/* Debit label */}
          <div className="absolute bottom-14 right-6 z-1">
            <p className="debit-text">
              {activeCard.card_provider === "MM" ? "debit" : ""}
            </p>
          </div>

          {/* Cardholder name */}
          <div className="flex justify-between items-center">
            <div className={`card-name ${nameMarginY}`}>
              {activeCard.name?.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Balance */}
      {!hideBalance && (
        <div className="relative w-[314px] max-[360px]:w-[300px]">
          <div className="balance-wrapper">
            <p className="balance-label">Card balance</p>
            <p className="balance-value">
              <span>{GetCurrenySymbol(activeCard?.currency_code, 18, 19)}</span>
              <span>
                {FormatBalance(activeCard?.card_balance ?? 0, false, true)}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
